import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { AssistantGroundingState } from "ai-assistant/grounding/assistant-grounding-state";
import { z } from "zod";

export type AssistantGroundingJSON = z.infer<typeof AssistantGroundingSchema.AssistantGroundingJSONZ>;
export type AssistantGroundingParams = z.infer<typeof AssistantGroundingSchema.AssistantGroundingZ>;

export default class AssistantGroundingSchema {
  public static readonly AssistantGroundingJSONZ = z.object({
    state: z.enum(AssistantGroundingState),
    /** Claims whose citation did not hold, plus markers naming a source never offered. */
    unverifiedClaims: z.number().nullish(),
    /** Claims the sources cover that the answer stated without a marker. */
    uncitedClaims: z.number().nullish(),
    /** Claims nothing ruled on, because the judge could not answer. */
    unruledClaims: z.number().nullish(),
  });

  public static readonly AssistantGroundingZ = z.object({
    state: z.enum(AssistantGroundingState),
    unverifiedClaims: z.number(),
    uncitedClaims: z.number(),
    unruledClaims: z.number(),
  });

  public static validate(values: unknown): AssistantGroundingParams {
    return parseOrThrow(AssistantGroundingSchema.AssistantGroundingZ, values, {
      logError: true,
      schemaName: "AssistantGroundingSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): AssistantGroundingJSON {
    return parseOrThrow(AssistantGroundingSchema.AssistantGroundingJSONZ, json, {
      logError: true,
      schemaName: "AssistantGroundingSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is AssistantGroundingParams {
    try {
      AssistantGroundingSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AssistantGroundingJSON {
    try {
      AssistantGroundingSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AssistantGroundingJSON): AssistantGroundingParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(AssistantGroundingSchema.AssistantGroundingJSONZ, json, {
      logError: true,
      schemaName: "AssistantGroundingSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AssistantGroundingSchema.codec.decode(parsed);
  }

  public static serialize(grounding: AssistantGroundingParams): AssistantGroundingJSON {
    if (isNullOrUndefined(grounding)) return grounding;
    const parsed = AssistantGroundingSchema.validate(grounding);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantGroundingSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AssistantGroundingSchema.AssistantGroundingJSONZ,
    AssistantGroundingSchema.AssistantGroundingZ,
    {
      // The counts default to zero rather than staying absent. A missing count is not a
      // different kind of answer from a zero one - the state is what carries that - and
      // making them optional here would push a `?? 0` into every reader.
      decode: (data) => ({
        state: data.state,
        unverifiedClaims: data.unverifiedClaims ?? 0,
        uncitedClaims: data.uncitedClaims ?? 0,
        unruledClaims: data.unruledClaims ?? 0,
      }),
      encode: (grounding) => ({
        state: grounding.state,
        unverifiedClaims: grounding.unverifiedClaims,
        uncitedClaims: grounding.uncitedClaims,
        unruledClaims: grounding.unruledClaims,
      }),
    }
  );
}
